import { useEffect, useRef } from "react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

type MetricCount =
  | { type: "plus"; to: number; suffix?: string }
  | { type: "value"; to: number; suffix: string }
  | { type: "static"; value: string }

const metrics = [
  { label: "Tape-Outs", count: { type: "plus" as const, to: 20 } },
  { label: "Process Range", count: { type: "static" as const, value: "180–5NM" } },
  { label: "Largest Die", count: { type: "value" as const, to: 650, suffix: "MM²" } },
  { label: "Avg. Experience", count: { type: "plus" as const, to: 20, suffix: " YRS" } },
]

function getInitialDisplay(count: MetricCount): string {
  if (count.type === "static") return count.value
  if (count.type === "plus") return `0+${count.suffix ?? ""}`
  return `0${count.suffix}`
}

function animateMetric(valueEl: HTMLSpanElement, count: MetricCount, delay: number) {
  if (count.type === "static") return
  if (count.type === "plus") {
    const counter = { n: 0 }
    gsap.to(counter, {
      n: count.to,
      duration: 2.2,
      delay,
      ease: "power2.out",
      snap: { n: 1 },
      onUpdate: () => {
        valueEl.textContent = `${Math.round(counter.n)}+${count.suffix ?? ""}`
      },
    })
    return
  }

  const counter = { n: 0 }
  gsap.to(counter, {
    n: count.to,
    duration: 2.2,
    delay,
    ease: "power2.out",
    snap: { n: 1 },
    onUpdate: () => {
      valueEl.textContent = `${Math.round(counter.n)}${count.suffix}`
    },
  })
}

export function TapeOutsSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLParagraphElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const glowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current || !headerRef.current || !gridRef.current) return

    const ctx = gsap.context(() => {
      if (glowRef.current) {
        gsap.fromTo(
          glowRef.current,
          { opacity: 0, scale: 0.85 },
          {
            opacity: 1,
            scale: 1,
            duration: 1.4,
            ease: "power2.out",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          },
        )
      }

      gsap.fromTo(
        headerRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 88%",
            toggleActions: "play none none reverse",
          },
        },
      )

      const cells = gridRef.current?.querySelectorAll("[data-metric-cell]")
      if (cells?.length) {
        gsap.fromTo(
          cells,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.14,
            ease: "power3.out",
            scrollTrigger: {
              trigger: gridRef.current,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          },
        )

        const runCountUp = () => {
          cells.forEach((cell, index) => {
            const valueEl = cell.querySelector<HTMLSpanElement>("[data-metric-value]")
            const metricIndex = Number(cell.getAttribute("data-metric-index") ?? index)
            const metric = metrics[metricIndex]
            if (!valueEl || !metric) return
            animateMetric(valueEl, metric.count, index * 0.15)
          })
        }

        const countTrigger = ScrollTrigger.create({
          trigger: gridRef.current,
          start: "top 85%",
          once: true,
          onEnter: runCountUp,
        })

        if (countTrigger.isActive) runCountUp()
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      id="tape-outs"
      className="relative overflow-hidden border-t border-border/30 py-32 pl-6 md:pl-28 pr-6 md:pr-12"
    >
      <div
        ref={glowRef}
        className="pointer-events-none absolute inset-x-0 -top-8 bottom-0 mx-auto w-full max-w-6xl opacity-0"
        style={{
          background:
            "radial-gradient(ellipse 95% 85% at 50% 0%, oklch(0.7 0.2 45 / 0.24) 0%, oklch(0.7 0.2 45 / 0.14) 28%, oklch(0.7 0.2 45 / 0.07) 52%, oklch(0.7 0.2 45 / 0.03) 72%, transparent 90%)",
        }}
        aria-hidden="true"
      />

      <p
        ref={headerRef}
        className="relative z-10 mb-16 text-center font-mono text-[10px] uppercase tracking-[0.35em] text-accent"
      >
        Measured In Tape-Outs
      </p>

      <div
        ref={gridRef}
        className="relative z-10 mx-auto grid max-w-6xl grid-cols-2 gap-px border border-border/30 bg-border/30 md:grid-cols-4"
      >
        {metrics.map((metric, index) => (
          <div
            key={metric.label}
            data-metric-cell
            data-metric-index={index}
            className="flex flex-col items-center justify-center bg-card/90 px-6 py-10 text-center md:px-8 md:py-14"
          >
            <span
              data-metric-value
              className="font-[var(--font-bebas)] text-4xl tracking-tight text-foreground tabular-nums md:text-5xl lg:text-6xl"
            >
              {getInitialDisplay(metric.count)}
            </span>
            <span className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              {metric.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}
